import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { WorkspaceService } from '@/api';
import { WorkspaceMenuService } from '@/api/generated/services/WorkspaceMenuService';
import type { WorkspaceResponse } from '@/api/types';
import type { WorkspaceMenuItemSchema } from '@/api/generated/models/WorkspaceMenuItemSchema';
import type { WorkspaceMenuSettingsReplaceRequest } from '@/api/generated/models/WorkspaceMenuSettingsReplaceRequest';
import { getCurrentOrgMemberInfo } from './authSlice';
import { handleAsyncThunkError } from './errorUtils';
import { storageService } from '@/services/storageService';

// 사이드바 메뉴 설정은 워크스페이스 단건/목록 응답의 menu_settings 필드에 함께 들어온다 (별도 GET 엔드포인트 없음).

// Storage keys
const SELECTED_WORKSPACE_KEY = 'dot_selected_workspace';

/**
 * Workspace storage utilities using storage service
 */
export const saveSelectedWorkspace = (workspace: WorkspaceResponse) => {
  storageService.setObject(SELECTED_WORKSPACE_KEY, workspace);
};

export const getSelectedWorkspace = (): WorkspaceResponse | null => {
  return storageService.getObject<WorkspaceResponse>(SELECTED_WORKSPACE_KEY);
};

export const clearSelectedWorkspace = () => {
  storageService.removeItem(SELECTED_WORKSPACE_KEY);
};

// Workspace state interface
interface WorkspaceState {
  workspaces: WorkspaceResponse[];
  selectedWorkspace: WorkspaceResponse | null;
  isLoading: boolean;
  error: string | null;
  hasInitialized: boolean;
}

// Initial state
const initialState: WorkspaceState = {
  workspaces: [],
  selectedWorkspace: getSelectedWorkspace(),
  isLoading: false,
  error: null,
  hasInitialized: false,
};

// Async thunks
export const fetchWorkspaces = createAsyncThunk(
  'workspace/fetchWorkspaces',
  async (params: { page?: number; perPage?: number } = {}, { dispatch, getState, rejectWithValue }) => {
    try {
      const response = await WorkspaceService.appsWorkspaceViewsListJoinedWorkspaces(params);
      const workspaces = (response.data || []) as WorkspaceResponse[];

      const state = getState() as any;
      const currentSelected = state.workspace.selectedWorkspace;

      if (currentSelected && workspaces.length > 0) {
        // Try to find the currently selected workspace in the fresh list
        const updatedWorkspace = workspaces.find(w => w.id === currentSelected.id);

        if (updatedWorkspace) {
          // Update localStorage with fresh workspace data
          saveSelectedWorkspace(updatedWorkspace);
          // Redux state will be updated in the reducer
        } else {
          // Selected workspace no longer exists, select first available
          await dispatch(selectWorkspaceAndFetchOrgMember(workspaces[0]));
        }
      } else if (!currentSelected && workspaces.length > 0) {
        // No workspace is selected and there are workspaces, select the first one with orgMember
        await dispatch(selectWorkspaceAndFetchOrgMember(workspaces[0]));
      }

      return workspaces;
    } catch (error: unknown) {
      return handleAsyncThunkError(error, rejectWithValue, '참여한 워크스페이스 목록 조회에 실패했습니다.');
    }
  }
);

export const fetchWorkspaceById = createAsyncThunk(
  'workspace/fetchWorkspaceById',
  async (workspaceId: string, { rejectWithValue }) => {
    try {
      const response = await WorkspaceService.appsWorkspaceViewsGetWorkspace({
        workspaceId
      });
      return response;
    } catch (error: unknown) {
      return handleAsyncThunkError(error, rejectWithValue, '워크스페이스 조회에 실패했습니다.');
    }
  }
);

export const selectWorkspaceAndFetchOrgMember = createAsyncThunk(
  'workspace/selectWorkspaceAndFetchOrgMember',
  async (workspace: WorkspaceResponse, { dispatch, rejectWithValue }) => {
    try {
      // Save workspace selection
      saveSelectedWorkspace(workspace);

      // Fetch org member info if org_id exists
      if (workspace.org_id) {
        await dispatch(getCurrentOrgMemberInfo(workspace.org_id));
      }

      // 워크스페이스 설정(ai_settings 등)은 Django admin에서 변경될 수 있어
      // 전환 시 상세를 비동기로 재조회해 최신화한다 (fulfilled 시 selectedWorkspace 갱신).
      dispatch(fetchWorkspaceById(workspace.id));

      return workspace;
    } catch (error: unknown) {
      return handleAsyncThunkError(error, rejectWithValue, '워크스페이스 선택 및 기관 멤버 정보 조회에 실패했습니다.');
    }
  }
);

/**
 * 사이드바 메뉴 설정 전체를 백엔드에 PUT하고, 응답으로 받은 정렬된 items를 store에 반영한다.
 * owner/admin만 호출 (서버는 403으로 차단됨).
 *
 * 성공 시 extraReducer가 현재 selectedWorkspace의 id와 매칭되면 menu_settings를 즉시 갱신한다.
 */
export const replaceCurrentWorkspaceMenuSettings = createAsyncThunk(
  'workspace/replaceCurrentWorkspaceMenuSettings',
  async (
    args: { workspaceId: string; items: WorkspaceMenuItemSchema[] },
    { rejectWithValue },
  ) => {
    try {
      const requestBody: WorkspaceMenuSettingsReplaceRequest = { items: args.items };
      const response = await WorkspaceMenuService.appsWorkspaceViewsReplaceWorkspaceMenuSettings({
        workspaceId: args.workspaceId,
        requestBody,
      });
      return {
        workspaceId: args.workspaceId,
        items: (response.items ?? []) as WorkspaceMenuItemSchema[],
      };
    } catch (error: unknown) {
      return handleAsyncThunkError(error, rejectWithValue, '사이드바 메뉴 설정 저장에 실패했습니다.');
    }
  }
);

// Workspace slice
const workspaceSlice = createSlice({
  name: 'workspace',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    selectWorkspace: (state, action: PayloadAction<WorkspaceResponse>) => {
      state.selectedWorkspace = action.payload;
      saveSelectedWorkspace(action.payload);
    },
    clearSelectedWorkspace: (state) => {
      state.selectedWorkspace = null;
      clearSelectedWorkspace();
    },
    initializeWorkspace: (state) => {
      const storedWorkspace = getSelectedWorkspace();
      if (storedWorkspace) {
        state.selectedWorkspace = storedWorkspace;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch workspaces
      .addCase(fetchWorkspaces.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchWorkspaces.fulfilled, (state, action) => {
        state.isLoading = false;
        state.workspaces = action.payload || [];
        state.error = null;
        state.hasInitialized = true;

        // Update selected workspace with fresh data if it exists in the list
        if (state.selectedWorkspace) {
          const updatedWorkspace = action.payload?.find(w => w.id === state.selectedWorkspace!.id);
          if (updatedWorkspace) {
            state.selectedWorkspace = updatedWorkspace;
          }
        }
      })
      .addCase(fetchWorkspaces.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.hasInitialized = true;
      })
      // Fetch workspace by ID — 선택 워크스페이스의 최신화(refresh) 용도.
      // 빠른 연속 전환 시 늦게 도착한 응답이 다른 선택을 덮어쓰지 않도록 id가 일치할 때만 반영한다.
      .addCase(fetchWorkspaceById.fulfilled, (state, action) => {
        if (state.selectedWorkspace && state.selectedWorkspace.id === action.payload.id) {
          state.selectedWorkspace = action.payload;
          saveSelectedWorkspace(action.payload);
        }
      })
      .addCase(fetchWorkspaceById.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // Select workspace and fetch org member
      .addCase(selectWorkspaceAndFetchOrgMember.fulfilled, (state, action) => {
        state.selectedWorkspace = action.payload;
      })
      .addCase(selectWorkspaceAndFetchOrgMember.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // Replace menu settings — 응답 items를 selectedWorkspace.menu_settings에 즉시 반영
      .addCase(replaceCurrentWorkspaceMenuSettings.fulfilled, (state, action) => {
        const { workspaceId, items } = action.payload;
        if (state.selectedWorkspace && state.selectedWorkspace.id === workspaceId) {
          state.selectedWorkspace = {
            ...state.selectedWorkspace,
            menu_settings: items,
          };
          // localStorage도 갱신 → 새로고침 후에도 유지
          saveSelectedWorkspace(state.selectedWorkspace);
        }
        // workspaces 목록에도 갱신
        const idx = state.workspaces.findIndex(w => w.id === workspaceId);
        if (idx >= 0) {
          state.workspaces[idx] = {
            ...state.workspaces[idx],
            menu_settings: items,
          };
        }
      });
  },
});

export const {
  clearError,
  selectWorkspace,
  clearSelectedWorkspace: clearWorkspace,
  initializeWorkspace
} = workspaceSlice.actions;

export default workspaceSlice.reducer;